import React, { useContext } from 'react';

import { Button } from '@99designs/common/components/Button';
import { Icon } from '@99designs/common/components/Icon';
import { DesignerLevel, DesignerSource, PenaltyState } from '__generated__/types';
import { handlePromise } from 'common/utils/Bugsnag';
import { Color, MAX_DESIGNERS } from 'projects/consts';
import { trackAddedDesignerEvent, trackRemovedDesignerEvent } from 'projects/events';
import AppendStructuredBriefDesigners from 'projects/mutations/AppendStructuredBriefDesigners';
import RemoveStructuredBriefDesigners from 'projects/mutations/RemoveStructuredBriefDesigners';
import BriefContext from './BriefContext';
import ColorComponent from './Color';

const AddDesignerButton = ({
    designer,
    position,
    designerSource,
    onAddDesigner,
}: {
    designer: {
        id: number;
        penaltyState: PenaltyState;
        displayName: string;
        profile: {
            designerLevel: DesignerLevel;
        };
    };
    position?: number;
    designerSource: DesignerSource;
    onAddDesigner?: () => void;
}) => {
    const { brief, themeColor = Color.createBlue } = useContext(BriefContext);
    return (
        <AppendStructuredBriefDesigners>
            {appendDesigners => (
                <RemoveStructuredBriefDesigners>
                    {removeDesigners => {
                        const isSuspended = designer.penaltyState === PenaltyState.SUSPENDED;
                        const isSelected = brief.designers.some(d => d.id == designer.id);
                        const shouldDisable =
                            brief.designers.length >= MAX_DESIGNERS || isSuspended;

                        return (
                            <ColorComponent color={themeColor}>
                                <Button
                                    isDisabled={shouldDisable && !isSelected}
                                    onClick={
                                        isSelected
                                            ? () => {
                                                  trackRemovedDesignerEvent({
                                                      id: brief.id,
                                                      relevant_ranking: position,
                                                      designer_id: designer.id,
                                                  });
                                                  handlePromise(
                                                      removeDesigners({
                                                          id: brief.id,
                                                          designerIds: [designer.id],
                                                      }),
                                                      'AddDesignerButton AddDesigners'
                                                  );
                                              }
                                            : () => {
                                                  trackAddedDesignerEvent({
                                                      id: brief.id,
                                                      relevant_ranking: position,
                                                      designer_id: designer.id,
                                                      designer_name: designer.displayName,
                                                      designer_level:
                                                          designer.profile.designerLevel,
                                                      designer_source: designerSource,
                                                  });
                                                  handlePromise(
                                                      appendDesigners({
                                                          id: brief.id,
                                                          designerIds: [designer.id],
                                                          source: designerSource,
                                                      }).then(() => {
                                                          if (onAddDesigner) {
                                                              onAddDesigner();
                                                          }
                                                      }),
                                                      'AddDesignerButton RemoveDesigners'
                                                  );
                                              }
                                    }
                                    variations={
                                        isSelected
                                            ? ['fluid', 'primary', 'chameleon']
                                            : ['fluid', 'secondary']
                                    }
                                    icon={isSelected ? <Icon type={'tick-3'} /> : undefined}
                                    iconAlignment="right"
                                >
                                    {isSuspended
                                        ? __('Designer suspended')
                                        : isSelected
                                        ? __('Added')
                                        : __('Add')}
                                </Button>
                            </ColorComponent>
                        );
                    }}
                </RemoveStructuredBriefDesigners>
            )}
        </AppendStructuredBriefDesigners>
    );
};

export default AddDesignerButton;
